import { Box, VStack, HStack, Text, Avatar, Badge, IconButton, useColorModeValue, useBreakpointValue } from '@chakra-ui/react';
import { FiUser, FiMoreVertical, FiBell } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import IconWrapper from './IconWrapper';

interface SubscribedCreator {
  id: string;
  name: string;
  avatar?: string;
  followers: number;
  newContent?: number;
  isOnline?: boolean;
  lastActive?: string;
  verified?: boolean;
}

interface SubscribedCreatorCardProps {
  creator: SubscribedCreator;
  onUnsubscribe?: (creatorId: string) => void;
  onNotificationToggle?: (creatorId: string, enabled: boolean) => void;
}

const SubscribedCreatorCard: React.FC<SubscribedCreatorCardProps> = ({ 
  creator, 
  onUnsubscribe, 
  onNotificationToggle 
}) => {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const titleColor = useColorModeValue('gray.900', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleCreatorClick = () => {
    navigate(`/creator/${creator.id}`);
  };

  const formatFollowerCount = (count: number): string => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      p={4}
      border="1px"
      borderColor={borderColor}
      _hover={{ 
        boxShadow: "md", 
        transform: "translateY(-2px)",
        borderColor: "purple.300"
      }}
      transition="all 0.2s ease-in-out"
      cursor="pointer"
      onClick={handleCreatorClick}
      w={{ base: "140px", sm: "160px", md: "200px" }}
      minW={{ base: "140px", sm: "160px", md: "200px" }}
    >
      <VStack align="center" gap={3}>
        {/* Avatar with Online Status */}
        <Box position="relative">
          <Avatar
            size={isMobile ? "lg" : "xl"}
            src={creator.avatar}
            name={creator.name}
            border="3px solid"
            borderColor={creator.isOnline ? "green.400" : "gray.300"}
          />
          
          {/* Online Status Indicator */}
          {creator.isOnline && (
            <Box
              position="absolute"
              bottom={0}
              right={0}
              w="12px"
              h="12px"
              bg="green.400"
              borderRadius="full"
              border="2px solid"
              borderColor={cardBg}
            />
          )}

          {/* Verified Badge */}
          {creator.verified && (
            <Box
              position="absolute"
              top={-1}
              right={-1}
              w="16px"
              h="16px"
              bg="blue.500"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="2px solid"
              borderColor={cardBg}
            >
              <Text fontSize="xs" color="white">✓</Text>
            </Box>
          )}
        </Box>

        {/* Creator Info */}
        <VStack align="center" gap={1} w="100%">
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={titleColor}
            textAlign="center"
            noOfLines={2}
            lineHeight="1.3"
          >
            {creator.name}
          </Text>
          
          <HStack gap={1}>
            <IconWrapper icon={FiUser} size={12} color={subtitleColor} />
            <Text fontSize="xs" color={subtitleColor}>
              {formatFollowerCount(creator.followers)} followers
            </Text>
          </HStack>

          {/* New Content Badge */}
          {creator.newContent && creator.newContent > 0 && (
            <Badge 
              colorScheme="red" 
              variant="solid" 
              size="sm"
              borderRadius="full"
            >
              {creator.newContent} new
            </Badge>
          )}

          {/* Last Active */}
          {creator.lastActive && (
            <Text fontSize="xs" color={subtitleColor} textAlign="center">
              {creator.lastActive}
            </Text>
          )}
        </VStack>

        {/* Action Buttons */}
        <HStack gap={1} w="100%" justify="center">
          <IconButton
            aria-label="Notification settings"
            icon={<IconWrapper icon={FiBell} size={14} />}
            size="sm"
            variant="ghost"
            colorScheme="purple"
            onClick={(e) => {
              e.stopPropagation();
              if (onNotificationToggle) {
                onNotificationToggle(creator.id, true);
              }
            }}
          />
          
          <IconButton
            aria-label="More options"
            icon={<IconWrapper icon={FiMoreVertical} size={14} />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            onClick={(e) => {
              e.stopPropagation();
              if (onUnsubscribe) {
                onUnsubscribe(creator.id);
              }
            }}
          />
        </HStack>
      </VStack>
    </Box>
  );
};

export default SubscribedCreatorCard;
